import type { ComponentProps, ComponentType } from "react";
import MiniAudioPlayer from "../methods/utils/MiniAudioPlayer/MiniAudioPlayer";
import MiniAudio from "../components/displayComponents/MiniAudio";
import { Socket } from "socket.io-client";
import {
  ReorderStreamsType,
  ReorderStreamsParameters,
  Participant,
  PrepopulateUserMediaType,
  PrepopulateUserMediaParameters,
  Stream,
  MiniAudioPlayerParameters,
  EventType,
} from "../@types/types";
import { Consumer } from "mediasoup-client/lib/types";

export interface ConsumerResumeParameters
  extends ReorderStreamsParameters,
    PrepopulateUserMediaParameters,
    MiniAudioPlayerParameters {
  nStream: MediaStream | null;
  allAudioStreams: (Stream | Participant)[];
  allVideoStreams: (Stream | Participant)[];
  streamNames: Stream[];
  audStreamNames: Stream[];
  updateMainWindow: boolean;
  shared: boolean;
  shareScreenStarted: boolean;
  screenId?: string;
  participants: Participant[];
  eventType: EventType;
  meetingDisplayType: string;
  mainScreenFilled: boolean;
  first_round: boolean;
  lock_screen: boolean;
  oldAllStreams: (Stream | Participant)[];
  adminVidID?: string;
  mainHeightWidth: number;
  member: string;
  audioOnlyStreams: React.JSX.Element[];
  gotAllVids: boolean;
  defer_receive: boolean;
  firstAll: boolean;
  remoteScreenStream: Stream[];
  hostLabel: string;
  whiteboardStarted: boolean;
  whiteboardEnded: boolean;

  miniAudioComponent?: ComponentType<ComponentProps<typeof MiniAudio>>;
  miniAudioPlayerComponent?: ComponentType<ComponentProps<typeof MiniAudioPlayer>>;

  updateUpdateMainWindow: (value: boolean) => void;
  updateAllAudioStreams: (value: (Stream | Participant)[]) => void;
  updateAllVideoStreams: (value: (Stream | Participant)[]) => void;
  updateStreamNames: (value: Stream[]) => void;
  updateAudStreamNames: (value: Stream[]) => void;
  updateNStream: (value: MediaStream) => void;
  updateMainHeightWidth: (value: number) => void;
  updateLock_screen: (value: boolean) => void;
  updateFirstAll: (value: boolean) => void;
  updateRemoteScreenStream: (value: Stream[]) => void;
  updateOldAllStreams: (value: (Stream | Participant)[]) => void;
  updateAudioOnlyStreams: (value: React.JSX.Element[]) => void;
  updateShareScreenStarted: (value: boolean) => void;
  updateGotAllVids: (value: boolean) => void;
  updateScreenId: (value: string) => void;
  updateDefer_receive: (value: boolean) => void;

  //mediasfu functions
  reorderStreams: ReorderStreamsType;
  prepopulateUserMedia: PrepopulateUserMediaType;
  getUpdatedAllParams: () => ConsumerResumeParameters;
  [key: string]: any;
}

interface ResumeParams {
  id: string;
  producerId: string;
  kind: string;
  rtpParameters: any;
}

export interface ConsumerResumeOptions {
  track: MediaStreamTrack;
  kind: string;
  remoteProducerId: string;
  params: ResumeParams;
  parameters: ConsumerResumeParameters;
  nsock: Socket;
  consumer: Consumer;
}

//export the type definition for the function
export type ConsumerResumeType = (
  options: ConsumerResumeOptions
) => Promise<void>;

const translationAudioRetryDelays = [0, 120, 240, 480];

const primeTranslationAudioElement = (audioElement: HTMLAudioElement, stream: MediaStream) => {
  if (audioElement.srcObject !== stream) {
    audioElement.srcObject = stream;
  }

  const tryPlay = () => {
    audioElement.play().catch(() => {});
  };

  translationAudioRetryDelays.forEach((delay) => {
    if (delay === 0) {
      tryPlay();
      return;
    }

    setTimeout(tryPlay, delay);
  });

  stream.getAudioTracks().forEach((audioTrack) => {
    audioTrack.addEventListener('unmute', tryPlay, { once: true });
  });
};

/**
 * Resumes a media consumer by handling the provided track and updating the relevant parameters.
 *
 * This function is a key part of the MediaSFU stream management pipeline and is a common
 * candidate for override via `uiOverrides.consumerResume`. Use the `wrap` pattern to inject
 * analytics, rate-limiting, or error handling without touching core logic.
 *
 * @param {ConsumerResumeOptions} options - The options for resuming the consumer.
 * @param {MediaStreamTrack} options.track - The media stream track to resume.
 * @param {string} options.remoteProducerId - The ID of the remote producer.
 * @param {ResumeParams} options.params - The parameters required for resuming the consumer.
 * @param {ConsumerResumeParameters} options.parameters - The parameters for updating the state.
 * @param {Socket} options.nsock - The socket instance for communication.
 * @param {Consumer} options.consumer - The consumer instance to resume.
 *
 * @returns {Promise<void>} A promise that resolves when the consumer is successfully resumed.
 *
 * @throws Will throw an error if the resumption fails or if there is an issue with the parameters.
 *
 * @example
 * // Direct usage
 * ```tsx
 * await consumerResume({
 *   track: mediaStreamTrack,
 *   remoteProducerId: 'producer-id',
 *   params: { id: 'consumer-id', producerId: 'producer-id', kind: 'audio', rtpParameters: {} },
 *   consumer: consumerInstance,
 *   parameters: { ...allParams },
 *   nsock: socketInstance,
 * });
 * ```
 *
 * @example
 * // Override via uiOverrides (add analytics)
 * ```tsx
 * const uiOverrides: MediasfuUICustomOverrides = {
 *   consumerResume: {
 *     wrap: (original) => async (options) => {
 *       const start = performance.now();
 *       await original(options);
 *       analytics.track('consumer_resume', {
 *         durationMs: performance.now() - start,
 *         consumerId: options.consumer.id,
 *       });
 *     },
 *   },
 * };
 * ```
 *
 * @example
 * // Full parameters object structure
 * ```tsx
 * const parameters: ConsumerResumeParameters = {
 *     nStream: null,
 *     allAudioStreams: [],
 *     allVideoStreams: [],
 *     streamNames: [],
 *     audStreamNames: [],
 *     updateMainWindow: false,
 *     shared: false,
 *     shareScreenStarted: false,
 *     participants: [],
 *     eventType: 'conference',
 *     meetingDisplayType: 'video',
 *     mainScreenFilled: false,
 *     first_round: false,
 *     lock_screen: false,
 *     oldAllStreams: [],
 *     adminVidID: null,
 *     mainHeightWidth: 0,
 *     member: 'John Doe',
 *     audioOnlyStreams: [],
 *     gotAllVids: false,
 *     defer_receive: false,
 *     firstAll: false,
 *     remoteScreenStream: [],
 *     hostLabel: 'host',
 *     whiteboardStarted: false,
 *     whiteboardEnded: false,
 *     updateUpdateMainWindow: (value) => { console.log('updated')},
 *     updateAllAudioStreams: (streams) => { console.log('updated')},
 *     updateAllVideoStreams: (streams) => { console.log('updated')},
 *     updateStreamNames: (streams) => { console.log('updated')},
 *     updateAudStreamNames: (streams) => { console.log('updated')},
 *     updateNStream: (stream) => { console.log('updated')},
 *     updateMainHeightWidth: (value) => { console.log('updated')},
 *     updateLock_screen: (value) => { console.log('updated')},
 *     updateFirstAll: (value) => { console.log('updated')},
 *     updateRemoteScreenStream: (streams) => { console.log('updated')},
 *     updateOldAllStreams: (streams) => { console.log('updated')},
 *     updateAudioOnlyStreams: (streams) => { console.log('updated')},
 *     updateShareScreenStarted: (value) => { console.log('updated')},
 *     updateGotAllVids: (value) => { console.log('updated')},
 *     updateScreenId: (id) => { console.log('updated')},
 *     updateDefer_receive: (value) => { console.log('updated')},
 *     reorderStreams: (params) => { console.log('reordered') }
 *     prepopulateUserMedia: (params) => { console.log('prepopulated') },
 *   },
 *   nsock: socketInstance,
 * };
 * 
 * consumerResume(options)
 *   .then(() => {
 *     console.log('Consumer resumed successfully');
 *   })
 *   .catch((error) => {
 *     console.error('Error resuming consumer:', error);
 *   });
 */


export const consumerResume = async ({
  track,
  remoteProducerId,
  params,
  parameters,
  nsock,
  consumer,
}: ConsumerResumeOptions): Promise<void> => {
  try {
    // Get updated parameters
    parameters = parameters.getUpdatedAllParams();

    // Destructure parameters
    let {
      nStream,
      allAudioStreams,
      allVideoStreams,
      streamNames,
      audStreamNames,
      updateMainWindow,
      shared,
      shareScreenStarted,
      screenId,
      participants,
      eventType,
      meetingDisplayType,
      mainScreenFilled,
      first_round,
      lock_screen,
      oldAllStreams,

      adminVidID,
      mainHeightWidth,
      member,
      audioOnlyStreams,
      gotAllVids,
      defer_receive,
      firstAll,
      remoteScreenStream,
      hostLabel,
      whiteboardStarted,
      whiteboardEnded,

      updateUpdateMainWindow,
      updateAllAudioStreams,
      updateAllVideoStreams,
      updateStreamNames,
      updateAudStreamNames,
      updateNStream,
      updateMainHeightWidth,
      updateLock_screen,
      updateFirstAll,
      updateRemoteScreenStream,
      updateOldAllStreams,
      updateAudioOnlyStreams,
      updateShareScreenStarted,
      updateGotAllVids,
      updateScreenId,
      updateDefer_receive,

      //mediasfu functions
      reorderStreams,
      prepopulateUserMedia,
      miniAudioComponent,
      miniAudioPlayerComponent,
    } = parameters;

    const MiniAudioComponentToUse = miniAudioComponent ?? MiniAudio;
    const MiniAudioPlayerComponentToUse =
      miniAudioPlayerComponent ?? MiniAudioPlayer;

    if (params.kind === "audio") {
      // Audio resumed

      // Check if this is a translation audio producer
      // Translation producers are tracked in activeTranslationProducerIds (a Set)
      const activeTranslationProducerIds = (parameters as any).activeTranslationProducerIds as Set<string> | undefined;
      const isTranslationAudio = activeTranslationProducerIds?.has(remoteProducerId) ||
        consumer.appData?.type === "translation" ||
        consumer.appData?.isTranslation;

      // Check if the participant with audioID == remoteProducerId has a valid videoID
      let participant = participants.filter(
        (p) => p.audioID === remoteProducerId
      );
      let name__ = participant.length > 0 ? participant[0].name || "" : "";

      if (name__ === member && !isTranslationAudio) return;

      // For translation audio, use the speaker's name from translation metadata if available
      if (isTranslationAudio && !name__) {
        const translationMeta = consumer.appData?.translationMeta as { speakerName?: string; speakerId?: string; language?: string } | undefined;
        name__ = translationMeta?.speakerName || `Translation-${remoteProducerId.slice(0, 8)}`;
      }

      // Skip if this is the member's own audio (but not translation audio)
      if (name__ === member && !isTranslationAudio) return;

      //find any participants with ScreenID not null and ScreenOn == true
      let screenParticipant_alt = participants.filter(
        (participant) => participant.ScreenID != null &&
          participant.ScreenOn == true &&
          participant.ScreenID != ""
      );
      if (screenParticipant_alt.length > 0) {
        screenId = screenParticipant_alt[0].ScreenID;
        updateScreenId(screenId!);
        if (!shared) {
          shareScreenStarted = true;
          updateShareScreenStarted(shareScreenStarted);
        }
      } else {
        if (whiteboardStarted && !whiteboardEnded) {
          // Whiteboard is active
        } else {
          screenId = "";
          updateScreenId(screenId);
          updateShareScreenStarted(false);
        }
      }

      // Media display and UI update to prioritize audio/video
      nStream = new MediaStream([track]);
      updateNStream(nStream);

      // For translation audio, add to translationStreams (separate array that's always rendered)
      if (isTranslationAudio) {
        // Get functional updater from parameters (avoids stale closure issues)
        const addTranslationStream = (parameters as any).addTranslationStream as ((element: React.JSX.Element) => void) | undefined;
        
        // Create simple audio element for translation audio (no UI needed)
        const translationTrack = (
          <audio
            key={`translation-${remoteProducerId}`}
            autoPlay
            data-translation-audio-player="true"
            data-producer-id={remoteProducerId}
            style={{ display: "none" }}
            ref={(ref) => {
              if (ref && nStream) {
                primeTranslationAudioElement(ref, nStream);
              }
            }}
            onLoadedMetadata={(event) => event.currentTarget.play().catch(() => {})}
            onCanPlay={(event) => event.currentTarget.play().catch(() => {})}
            onStalled={(event) => event.currentTarget.play().catch(() => {})}
          />
        );

        // Add to translationStreams using functional update
        if (addTranslationStream) {
          addTranslationStream(translationTrack);
        }

        // Add to allAudioStreams for tracking
        allAudioStreams = [
          ...allAudioStreams,
          { producerId: remoteProducerId, stream: nStream },
        ];
        updateAllAudioStreams(allAudioStreams);

        // Add to audStreamNames for tracking
        audStreamNames = [
          ...audStreamNames,
          { producerId: remoteProducerId, name: name__ },
        ];
        updateAudStreamNames(audStreamNames);

        return;
      }

      // Create MiniAudioPlayer track (for non-translation audio)
      let nTrack = (
        <MiniAudioPlayerComponentToUse
          stream={nStream}
          remoteProducerId={remoteProducerId}
          parameters={parameters}
          consumer={consumer}
          MiniAudioComponent={MiniAudioComponentToUse}
          miniAudioProps={{
            customStyle: { backgroundColor: "gray" },
            name: name__,
            showWaveform: true,
            overlayPosition: "topRight",
            barColor: "white",
            textColor: "white",
            imageSource: "https://mediasfu.com/images/logo192.png",
            roundedImage: true,
            imageStyle: {},
          }}
        />
      );

      // Add to audioOnlyStreams array
      audioOnlyStreams.push(nTrack);
      updateAudioOnlyStreams(audioOnlyStreams);

      // Add to allAudioStreams array; add producerId, stream
      allAudioStreams = [
        ...allAudioStreams,
        { producerId: remoteProducerId, stream: nStream },
      ];
      updateAllAudioStreams(allAudioStreams);

      let name;

      try {
        name = participant[0].name;
      } catch {
        name = "";
      }

      if (name) {
        // Add to audStreamNames array; add producerId, name
        audStreamNames = [
          ...audStreamNames,
          { producerId: remoteProducerId, name: name__ },
        ];
        updateAudStreamNames(audStreamNames);

        if (!mainScreenFilled && participant[0].islevel === "2") {
          updateMainWindow = true;
          updateUpdateMainWindow(updateMainWindow);
          await prepopulateUserMedia({
            name: hostLabel,
            parameters: { ...parameters, audStreamNames, allAudioStreams },
          });
          updateMainWindow = false;
          updateUpdateMainWindow(updateMainWindow);
        }
      } else {
        // No participant found and not translation audio - skip
        return;
      }

      // Checks for display type and updates the UI
      let checker;
      let alt_checker = false;

      if (meetingDisplayType == "video") {
        checker =
          participant[0].videoID != null &&
          participant[0].videoID != "" &&
          participant[0].videoID != undefined;
      } else {
        checker = true;
        alt_checker = true;
      }

      if (checker) {
        if (shareScreenStarted || shared) {
          if (!alt_checker) {
            await reorderStreams({
              parameters: { ...parameters, audStreamNames, allAudioStreams },
            });
          }
        } else {
          if (alt_checker && meetingDisplayType != "video") {
            await reorderStreams({
              add: false,
              screenChanged: true,
              parameters: { ...parameters, audStreamNames, allAudioStreams },
            });
          }
        }
      }
    } else {
      // Video resumed
      nStream = new MediaStream([track]);
      updateNStream(nStream);

      //find any participants with ScreenID not null and ScreenOn == true
      let screenParticipant_alt = participants.filter(
        (participant) => participant.ScreenID != null &&
          participant.ScreenOn == true &&
          participant.ScreenID != ""
      );
      if (screenParticipant_alt.length > 0) {
        screenId = screenParticipant_alt[0].ScreenID;
        updateScreenId(screenId!);
        if (!shared) {
          shareScreenStarted = true;
          updateShareScreenStarted(shareScreenStarted);
        }
      } else {
        if (whiteboardStarted && !whiteboardEnded) {
          // Whiteboard is active
        } else {
          screenId = "";
          updateScreenId(screenId);
          updateShareScreenStarted(false);
        }
      }

      // Check for display type and update the UI
      if (remoteProducerId == screenId) {
        // Put on main screen for screen share
        updateMainWindow = true;
        updateUpdateMainWindow(updateMainWindow);
        remoteScreenStream = [
          { producerId: remoteProducerId, stream: nStream },
        ];
        updateRemoteScreenStream(remoteScreenStream);

        if (eventType == "conference") {
          if (shared || shareScreenStarted || (whiteboardStarted && !whiteboardEnded)) {
            if (mainHeightWidth == 0) {
              updateMainHeightWidth(84);
            }
          } else {
            if (mainHeightWidth > 0) {
              updateMainHeightWidth(0);
            }
          }
        }

        if (!lock_screen) {
          await prepopulateUserMedia({ name: hostLabel, parameters });
          await reorderStreams({
            add: false,
            screenChanged: true,
            parameters: { ...parameters, remoteScreenStream, allVideoStreams },
          });
        } else {
          if (!first_round) {
            await prepopulateUserMedia({
              name: hostLabel,
              parameters: {
                ...parameters,
                remoteScreenStream,
                allVideoStreams,
              },
            });
            await reorderStreams({
              add: false,
              screenChanged: true,
              parameters: {
                ...parameters,
                remoteScreenStream,
                allVideoStreams,
              },
            });
          }
        }

        lock_screen = true;
        updateLock_screen(lock_screen);
        firstAll = true;
        updateFirstAll(firstAll);
      } else {
        // Non-screen share video resumed

        // Operations to add video to the UI (either main screen or mini screen)
        parameters = parameters.getUpdatedAllParams();

        // Get the name of the participant with videoID == remoteProducerId
        let participant = participants.filter(
          (participant) => participant.videoID == remoteProducerId
        );

        if (
          participant.length > 0 &&
          participant[0].name != null &&
          participant[0].name != "" &&
          participant[0].name != undefined &&
          participant[0].name !== member
        ) {
          allVideoStreams = [
            ...allVideoStreams,
            { producerId: remoteProducerId, stream: nStream, socket_: nsock },
          ];
          updateAllVideoStreams(allVideoStreams);
        }

        if (participant.length > 0) {
          let name = participant[0].name;
          streamNames = [
            ...streamNames,
            { producerId: remoteProducerId, name: name || "" },
          ];
          updateStreamNames(streamNames);
        }

        // If not screenshare, filter out the stream that belongs to the participant with isAdmin = true and islevel == '2' (host)
        // Find the ID of the participant with isAdmin = true and islevel == '2'
        if (!shareScreenStarted) {
          let admin = participants.filter(
            (participant) => (participant.isAdmin == true || participant.isHost == true) && participant.islevel == "2"
          );
          // Remove video stream with producerId == admin.id
          // Get the videoID of the admin

          if (admin.length > 0) {
            adminVidID = admin[0].videoID;

            if (adminVidID != null && adminVidID != "") {
              let oldAllStreams_: (Stream | Participant)[] = [];
              // Check if the length of allVideoStreams is > 0
              if (oldAllStreams.length > 0) {
                oldAllStreams_ = oldAllStreams;
              }

              oldAllStreams = allVideoStreams.filter(
                (streame) => streame.producerId == adminVidID
              );
              updateOldAllStreams(oldAllStreams);

              if (oldAllStreams.length < 1) {
                oldAllStreams = oldAllStreams_;
                updateOldAllStreams(oldAllStreams);
              }

              allVideoStreams = allVideoStreams.filter(
                (streame) => streame.producerId != adminVidID
              );
              updateAllVideoStreams(allVideoStreams);

              if (remoteProducerId == adminVidID) {
                updateMainWindow = true;
              }
            }

            gotAllVids = true;
            updateGotAllVids(gotAllVids);
          }
        } else {
          // Check if the videoID is either that of the admin or that of the screen participant
          let screenParticipant = participants.filter(
            (participant) => participant.ScreenID == screenId
          );

          // See if producerId is that of admin videoID or screenParticipant videoID
          let adminVidID;

          let screenParticipantVidID;
          if (screenParticipant.length > 0) {
            screenParticipantVidID = screenParticipant[0].videoID;
          }

          if (
            (adminVidID != null && adminVidID != "") ||
            (screenParticipantVidID != null && screenParticipantVidID != "")
          ) {
            if (
              adminVidID == remoteProducerId ||
              screenParticipantVidID == remoteProducerId
            ) {
              await reorderStreams({
                parameters: { ...parameters, allVideoStreams },
              });
              return;
            }
          }
        }

        // Update the UI
        if (lock_screen || shared) {
          defer_receive = true;
          updateDefer_receive(defer_receive);

          if (!first_round) {
            await reorderStreams({
              add: false,
              screenChanged: true,
              parameters: { ...parameters, allVideoStreams },
            });
          }
        } else {
          await reorderStreams({
            add: false,
            screenChanged: true,
            parameters: { ...parameters, allVideoStreams },
          });
        }
      }
    }
  } catch (error) {
    console.log("consumerResume error", error);
    // throw error;
  }
};
